import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";
import { Card, message } from "antd";
import { useCohortById, useCohortSessions, useCohortResponses } from "../../api/CohortsAPI";
import { useCompetenciesCount, useAllCompetencies } from "../../api/CompetenciesAPI";
import CohortCinemaMapPDF from "../../components/cohorts/CohortCinemaMapPDF";
import TopCompetenciesTablePDF from "../../components/cohorts/TopCompetenciesTablePDF";
import CategoryDistributionTablePDF from "../../components/cohorts/CategoryDistributionTablePDF";
import CohortReportWeb from "../../components/cohorts/CohortReportWeb";

// Format date helper function
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};


const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: "Poppins",
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 10,
  },
  cohortDetailsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  leftDetails: {
    fontSize: 8,
  },
  rightDetails: {
    fontSize: 8,
    textAlign: "right",
  },
  detailLine: {
    marginBottom: 3,
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    marginTop: 4,
    marginBottom: 4,
  },
  sectionHeader: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 15,
  },
});

const CohortReportView = () => {
  const { cohortId } = useParams();
  const navigate = useNavigate();
  const { data: cohort, isLoading: cohortLoading } = useCohortById(cohortId);
  const { data: participants, isLoading: participantsLoading } = useCohortSessions(cohortId);
  const { data: totalCompetenciesInDb } = useCompetenciesCount();
  const { data: allCompetenciesData, isLoading: competenciesLoading } = useAllCompetencies();
  const { data: cohortResponses, isLoading: responsesLoading } = useCohortResponses(cohortId, { completedOnly: true });

  // Redirect if cohort is not archived
  useEffect(() => {
    if (cohort && cohort.status !== "archived") {
      message.warning("Cohort report is only available for archived cohorts");
      navigate(`/cohorts/${cohortId}`);
    }
  }, [cohort, cohortId, navigate]);

  const handleDownloadPDF = async () => {
    // Wait for all data to be loaded
    if (cohortLoading || participantsLoading || competenciesLoading || responsesLoading) {
      message.warning("Please wait for data to load before downloading PDF");
      return;
    }

    try {

      // Process top competencies data for PDF (same logic as web version)
      const topCompetenciesData = {};
      
      if (cohortResponses?.competency_counts && allCompetenciesData) {
        // Create lookup map for competency details
        const competencyMap = new Map();
        allCompetenciesData.forEach(comp => {
          competencyMap.set(comp.id, {
            name: comp.name,
            category: comp.category,
            abilityName: comp.abilities?.name || 'Unknown',
            phase: comp.abilities?.phase || 'Unknown'
          });
        });

        // Group competency counts by phase
        const phaseGroups = {};
        const phases = ['Empathize', 'Define', 'Ideate', 'Prototype', 'Test', 'Communicate'];
        
        phases.forEach(phase => {
          phaseGroups[phase] = [];
        });

        // Process each competency count
        cohortResponses.competency_counts.forEach(countItem => {
          const competencyDetails = competencyMap.get(countItem.competency_id);
          if (competencyDetails) {
            const phase = competencyDetails.phase;
            const percentage = Math.round((countItem.count / (participants?.length || 1)) * 100);
            
            phaseGroups[phase]?.push({
              competencyId: countItem.competency_id,
              abilityName: competencyDetails.abilityName,
              competencyName: competencyDetails.name,
              category: competencyDetails.category,
              count: countItem.count,
              percentage: percentage
            });
          }
        });

        // Sort each phase by count (descending) and take top 10
        phases.forEach(phase => {
          if (phaseGroups[phase]) {
            phaseGroups[phase].sort((a, b) => {
              if (b.count !== a.count) {
                return b.count - a.count;
              }
              return a.competencyName.localeCompare(b.competencyName);
            });
            phaseGroups[phase] = phaseGroups[phase].slice(0, 10);
          }
        });

        Object.assign(topCompetenciesData, phaseGroups);
      }

      const doc = (
        <Document>
          {/* Page 1: Overview */}
          <Page size="A4" style={styles.page}>
            <View style={styles.header}>
              <Text style={styles.title}>
                Cohort Competency Mapping Report
              </Text>

              <View style={styles.cohortDetailsContainer}>
                <View style={styles.leftDetails}>
                  <Text style={styles.detailLine}>
                    Cohort ID: {cohort?.id || 'N/A'}
                  </Text>
                  <Text style={styles.detailLine}>
                    Cohort Name: {cohort?.name || 'Untitled Cohort'}
                  </Text>
                  <Text style={styles.detailLine}>
                    Created: {formatDate(cohort?.created_at)}
                  </Text>
                  <Text style={styles.detailLine}>
                    Status: {cohort?.status || 'N/A'}
                  </Text>
                </View>

                <View style={styles.rightDetails}>
                  <Text style={styles.detailLine}>
                    Total Participants: {participants?.length || 0}
                  </Text>
                  <Text style={styles.detailLine}>
                    Completed Sessions: {participants?.filter(p => p.status === 'completed')?.length || 0}
                  </Text>
                  <Text style={styles.detailLine}>
                    Total Competencies Selected: {cohortResponses?.response_count || 0}
                  </Text>
                  <Text style={styles.detailLine}>
                    Unique Competencies: {cohortResponses?.unique_competencies || 0}
                  </Text>
                </View>
              </View>

              <View style={styles.divider} />
            </View>

            {<View>
              <Text style={styles.sectionHeader}>The Big Picture</Text>
              <CohortCinemaMapPDF
                cohortResponses={cohortResponses}
                allCompetenciesData={allCompetenciesData}
              />
            </View>}
          </Page>

          {/* Page 2: Top Competencies */}
          {<Page size="A4" style={styles.page}>
            <View style={styles.header}>
              <Text style={styles.title}>Top Selected Competencies by Phase</Text>
              <View style={styles.divider} />
            </View>

            <TopCompetenciesTablePDF 
              topCompetenciesData={topCompetenciesData}
              totalParticipants={participants?.length || 0}
            />
          </Page>}

          {/* Page 3: Category Analysis */}
          {<Page size="A4" style={styles.page}>
            <View style={styles.header}>
              <Text style={styles.title}>Category Analysis</Text>
              <View style={styles.divider} />
            </View>

           <CategoryDistributionTablePDF 
              cohortResponses={cohortResponses}
              allCompetenciesData={allCompetenciesData}
            />
          </Page>}
        </Document>
      );

      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Cohort-Report-${cohort?.id || "Unknown"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      message.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      message.error("Failed to generate PDF. Please try again.");
    }
  };

  if (cohortLoading || participantsLoading || competenciesLoading || responsesLoading) {
    return (
      <div className="h-full py-6 px-10 flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-medium mb-2">Loading cohort report...</div>
          <div className="text-gray-500">Please wait while we gather the data</div>
        </div>
      </div>
    );
  }

  if (!cohort) {
    return (
      <div className="h-full py-6 px-10 flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-medium mb-2">Cohort not found</div>
          <div className="text-gray-500">The requested cohort could not be found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full p-6 flex flex-col gap-6">
      
      <Card className="mb-4 bg-white text-primary rounded-lg">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-4">
            <h1 className="font-semibold text-lg m-0">Cohort Report</h1>
            
            <button
          className={`text-white text-xs px-3 py-1.5 rounded transition-colors duration-200 ${
            cohortLoading || participantsLoading || competenciesLoading || responsesLoading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-primary hover:bg-primaryLight'
          }`}
          onClick={handleDownloadPDF}
          disabled={cohortLoading || participantsLoading || competenciesLoading || responsesLoading}
        >
          {cohortLoading || participantsLoading || competenciesLoading || responsesLoading
            ? 'Loading...'
            : 'Download PDF'
          }
        </button>
        <button
              className="border-primary border-2 hover:bg-primary hover:text-white text-primary text-xs px-3 py-1.5 rounded transition-colors duration-200"
              onClick={() => navigate(`/cohorts/${cohortId}`)}
            >
              Back to Cohort
            </button>
          </div>
          <p className="text-gray-500 m-0">Comprehensive analysis of competency selections across the cohort.</p>
        </div>
      </Card>
      <CohortReportWeb 
        cohortResponses={cohortResponses} 
        cohort={cohort}
        participants={participants}
      />
    </div>
  );
};

export default CohortReportView;

